import type {
  BudgetSheetCellValue,
  BudgetSheetColumn,
  MetricSheetState
} from '../../services/projectAutofillDraftService.ts'
import { buildBudgetBinding, type BudgetSheetRubroRef } from './budgetSheetStateUtils.ts'

export const METRIC_TEMPLATE_VERSION = 'edify-metric-v1'

export const METRIC_BUILTIN_COLUMN_IDS = ['item', 'unit', 'quantity', 'notes'] as const

export const MAX_METRIC_SHEET_ROWS = 200
export const MAX_METRIC_CELL_CHARS = 2000

function newRowId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `r-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

export function defaultMetricColumnsV1(): BudgetSheetColumn[] {
  return METRIC_BUILTIN_COLUMN_IDS.map((id) => ({ id }))
}

export function buildDefaultMetricSheetState(rubro: BudgetSheetRubroRef): MetricSheetState {
  return {
    templateVersion: METRIC_TEMPLATE_VERSION,
    binding: buildBudgetBinding(rubro),
    columns: defaultMetricColumnsV1(),
    rows: [
      {
        rowId: newRowId(),
        cells: { item: '', unit: '', quantity: '', notes: '' }
      }
    ],
    source: 'user_edited',
    updatedAt: new Date().toISOString()
  }
}

export function attachMetricBindingToState(
  rubro: BudgetSheetRubroRef,
  state: MetricSheetState
): MetricSheetState {
  return {
    ...state,
    binding: buildBudgetBinding(rubro)
  }
}

function normalizeCellString(raw: string): string {
  return raw.length > MAX_METRIC_CELL_CHARS ? raw.slice(0, MAX_METRIC_CELL_CHARS) : raw
}

function parseCellForColumn(colId: string, raw: string): BudgetSheetCellValue {
  const trimmed = raw.trim()
  if (trimmed === '') return ''
  if (colId === 'quantity') {
    const n = Number(trimmed.replace(',', '.'))
    if (Number.isFinite(n)) return n
  }
  return normalizeCellString(raw)
}

export function applyMetricSheetCellChange(
  state: MetricSheetState,
  rowId: string,
  colId: string,
  raw: string
): MetricSheetState {
  const rows = state.rows.map((row) => {
    if (row.rowId !== rowId) return row
    return {
      ...row,
      cells: {
        ...row.cells,
        [colId]: parseCellForColumn(colId, raw)
      }
    }
  })
  return {
    ...state,
    rows,
    source: 'user_edited',
    updatedAt: new Date().toISOString()
  }
}

export function addMetricSheetRow(state: MetricSheetState): MetricSheetState {
  if (state.rows.length >= MAX_METRIC_SHEET_ROWS) return state
  const cells: Record<string, BudgetSheetCellValue> = {}
  for (const c of state.columns) cells[c.id] = ''
  return {
    ...state,
    rows: [...state.rows, { rowId: newRowId(), cells }],
    source: 'user_edited',
    updatedAt: new Date().toISOString()
  }
}

export function removeMetricSheetRow(state: MetricSheetState, rowId: string): MetricSheetState {
  return {
    ...state,
    rows: state.rows.filter((r) => r.rowId !== rowId),
    source: 'user_edited',
    updatedAt: new Date().toISOString()
  }
}

export function defaultMetricColumnCaptionEs(id: string): string {
  switch (id) {
    case 'item':
      return 'Ítem'
    case 'unit':
      return 'Unidad'
    case 'quantity':
      return 'Cantidad'
    case 'notes':
      return 'Notas'
    default:
      return id
  }
}

export function columnHeaderLabel(col: BudgetSheetColumn): string {
  if (col.displayLabel && String(col.displayLabel).trim()) return String(col.displayLabel).trim()
  return defaultMetricColumnCaptionEs(col.id)
}

export function isMetricSheetTemplateSupported(state: MetricSheetState): boolean {
  return state.templateVersion === METRIC_TEMPLATE_VERSION
}

export function validateMetricSheetStateClient(state: MetricSheetState): string | null {
  if (!isMetricSheetTemplateSupported(state)) return 'Versión de plantilla de cómputo no soportada'
  if (!state.binding?.tempId?.trim()) return 'binding.tempId requerido'
  if (!state.binding?.rubroLabel?.trim()) return 'binding.rubroLabel requerido'
  if (!state.columns.length) return 'Se requiere al menos una columna'
  if (state.rows.length > MAX_METRIC_SHEET_ROWS) return 'máximo 200 filas'
  let hasItem = false
  for (const row of state.rows) {
    const item = row.cells?.item
    if (item != null && String(item).trim() !== '') hasItem = true
  }
  if (!hasItem) return 'Agregá al menos un ítem en el cómputo'
  return null
}

export function serializeMetricSheetForDirtySnapshot(state: MetricSheetState | undefined): string {
  if (!state) return ''
  return JSON.stringify({
    templateVersion: state.templateVersion,
    source: state.source,
    rows: state.rows.map((r) => ({ rowId: r.rowId, cells: r.cells }))
  })
}
